import type { MapCategoryInput, MapConfigInput } from './schema';

/**
 * Groups used by the map selector.
 *
 * Declared so the selector can render a human name, icon and stable order for a
 * group without any component knowing what a category is. `order` sorts the
 * groups; maps inside a group are sorted by their own `order`.
 */
export const mapCategories: MapCategoryInput[] = [
	{ id: 'worlds', name: 'Worlds', icon: 'Globe', order: 0 }
];

/**
 * Every map the site knows about.
 *
 * This array is the whole map roster, the selector, the routes, the dimension
 * switcher and the search index are all derived from it. There is no fixed
 * number of maps and no component holds a map name.
 *
 * The five entries below mirror the map configs under
 * `plugins/BlueMap/maps/*.conf` on the Citrus Meadow server, one per rendered
 * world. Note that `id` and `bluemap.mapId` are deliberately different values:
 * BlueMap names a map after its config file, and those names contain an
 * underscore (`citrus_meadows`), which is not usable in a URL segment. The site
 * keeps a hyphenated `id` — which the URL slug derives from — and points at
 * BlueMap's own name through `bluemap.mapId`.
 *
 * Field notes:
 * - `id`        the internal identity: DOM ids, object keys, what other config references.
 * - `slug`      the URL segment, i.e. `/<slug>/`. Defaults to `id`, so it is only written
 *               out where the two differ. Slugs are hyphens-only and must be unique.
 * - `enabled`   false removes the map from the site entirely.
 * - `visible`   false keeps it reachable by direct URL but hides it from the selector.
 * - `selectable` false makes it read-only (no route, no selector entry).
 * - `default`   the fallback map when no map is in the URL. At most one enabled map
 *               may set this; `validate.ts` enforces it.
 * - `group`     links sibling maps that are dimensions of the same world, which is
 *               what the dimension switcher renders. The worlds below are separate
 *               worlds rather than dimensions of one, so none of them shares a group.
 * - `features`  narrows the site-wide UI features for this map only. It can only
 *               turn things *off*: a globally disabled feature stays disabled.
 *
 * Adding or removing a world is an edit to this array plus a matching
 * `plugins/BlueMap/maps/<name>.conf` on the server. Removing an entry here only
 * drops it from the site; BlueMap keeps rendering it either way.
 */
export const maps: MapConfigInput[] = [
	{
		id: 'citrus-meadows',
		name: 'Citrus Meadows',
		description: 'The overworld.',
		icon: 'TreePine',
		category: 'worlds',
		order: 0,
		default: true,
		tags: ['citrusmeadows', 'overworld', 'survival'],
		bluemap: { server: 'main', mapId: 'citrus_meadows' }
	},
	{
		id: 'spawn-swamp',
		name: 'Spawn Swamp',
		description: 'The spawn swamp.',
		icon: 'Home',
		category: 'worlds',
		order: 10,
		tags: ['spawnswamp', 'spawn', 'overworld'],
		bluemap: { server: 'main', mapId: 'spawn_swamp' }
	},
	{
		id: 'asset-world',
		name: 'Asset World',
		description: 'Build and asset world.',
		icon: 'Boxes',
		category: 'worlds',
		order: 20,
		tags: ['assetworld', 'assets', 'builds', 'overworld'],
		bluemap: { server: 'main', mapId: 'asset_world' }
	},
	{
		id: 'broken-canopy',
		name: 'Broken Canopy',
		description: 'The Broken Canopy world.',
		icon: 'Layers',
		category: 'worlds',
		order: 30,
		tags: ['brokencanopy', 'overworld'],
		bluemap: { server: 'main', mapId: 'broken_canopy' }
	},
	{
		id: 'aetherbark-village',
		name: 'Aetherbark Village',
		description: 'The village world.',
		icon: 'MapPin',
		category: 'worlds',
		order: 40,
		tags: ['aetherbarkvillage', 'village', 'overworld'],
		bluemap: { server: 'main', mapId: 'aetherbark_village' }
	},

	/*
	 * Disabled maps stay in the configuration on purpose: flipping `enabled` to true
	 * is all it takes to bring one back, and nothing else in the codebase changes.
	 */
	{
		id: 'flat',
		name: 'Flat',
		description: 'The flat test world.',
		icon: 'Layers',
		category: 'worlds',
		enabled: false,
		order: 999,
		bluemap: { server: 'main', mapId: 'flat' }
	}
];
